import { useState } from 'react'
import {
  CopyOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  FileSearchOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { DropdownMenu } from '../../../components/ui/DropdownMenu'
import { cx } from '../../../components/ui/cx'
import { formatPrice } from '../../parts/format'
import type {
  CatalogSuggestion,
  FileToResolve,
  IdentifiedPart,
  StagingRowSignal,
} from '../staging-types'
import {
  ANALYSIS_SIGNAL_REASON_LABEL,
  RESOLUTION_ISSUE_LABEL,
  stagingRowSignalTintClass,
} from '../staging-types'

// Issue-badge color (Fictiv Platform.dc.html:7330-7335/7479-7482's `cfg`):
// orphan/no-match are amber warnings (not red errors — resolvable in-place,
// never blocking), duplicate is purple, no-drawing is blue/info.
const ISSUE_TONE: Record<FileToResolve['issue'], 'warning' | 'purple' | 'info'> = {
  'orphan-drawing': 'warning',
  'no-drawing': 'info',
  duplicate: 'purple',
  'no-match': 'warning',
}

// Distinct per-issue icon (`Fictiv Platform.dc.html:2878-2881`) — `duplicate`
// keeps its own "copy" glyph; the other three each get a semantically
// matched icon instead of sharing one generic exclamation mark, so the icon
// itself (not just the badge tone/text) distinguishes the four issue types.
const ISSUE_ICON: Record<FileToResolve['issue'], typeof CopyOutlined> = {
  'orphan-drawing': DisconnectOutlined,
  'no-drawing': InfoCircleOutlined,
  duplicate: CopyOutlined,
  'no-match': FileSearchOutlined,
}

const ISSUE_DESCRIPTION: Record<FileToResolve['issue'], string> = {
  'orphan-drawing': 'This drawing has no matching CAD model — attach it to a part or remove it.',
  'no-drawing': 'This CAD model has no drawing yet — attach one or continue without it.',
  duplicate: 'This file looks like a duplicate of another upload in this session.',
  'no-match': 'We couldn’t match this file to a part or a catalog item.',
}

export interface FileToResolveRowProps {
  readonly file: FileToResolve
  readonly identifiedParts: readonly IdentifiedPart[]
  readonly onAssign: (targetPartId: string) => void
  readonly onCreatePart: () => void
  /** UID-222 #3 — resolve with a chosen DEMO catalog suggestion's id. */
  readonly onMatchToCatalog: (catalogId: string) => void
  /** UID-222 #3 — free-text query against the MOCK/DEMO catalog. */
  readonly onSearchCatalog: (query: string) => Promise<readonly CatalogSuggestion[]>
  readonly onKeepAsCustom: () => void
  readonly onRemove: () => void
  /** UID-255 (additive/optional) — this row's auto-quote-vs-manual signal,
   * looked up by `file.fileId`. Absent ⇒ no tint/chip (defensive fallback). */
  readonly analysisSignal?: StagingRowSignal | undefined
}

/**
 * Files-to-resolve row (S-F, UID-190/UID-222, `Fictiv Platform.dc.html:2880-
 * 2937/2907`): an issue icon + uppercase issue badge + description, then
 * issue-typed actions against the server-authoritative `resolveFile`
 * endpoint. `no-match` rows get an inline "Match to catalog" search box
 * (`searchStagingCatalog`) rather than a single blind action — the results
 * are DEMO/MOCK data, clearly labeled, never a real pricing source.
 */
export function FileToResolveRow({
  file,
  identifiedParts,
  onAssign,
  onCreatePart,
  onMatchToCatalog,
  onSearchCatalog,
  onKeepAsCustom,
  onRemove,
  analysisSignal,
}: FileToResolveRowProps) {
  // UID-402 — shared helper. These rows ARE the needs-resolution rows, so this
  // is the one place the orange wash still lands.
  const signalTintClass = stagingRowSignalTintClass(analysisSignal)
  const [catalogQuery, setCatalogQuery] = useState('')
  const [catalogResults, setCatalogResults] = useState<readonly CatalogSuggestion[] | null>(null)
  const [catalogBusy, setCatalogBusy] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const isNoMatch = file.issue === 'no-match'
  const IssueIcon = ISSUE_ICON[file.issue]

  async function handleCatalogSearch() {
    setCatalogBusy(true)
    setCatalogError(null)
    try {
      const suggestions = await onSearchCatalog(catalogQuery)
      setCatalogResults(suggestions)
    } catch (error) {
      setCatalogError(
        error instanceof Error ? error.message : 'We couldn’t search the catalog. Try again.',
      )
    } finally {
      setCatalogBusy(false)
    }
  }

  return (
    <li className={cx('staging-file-row', signalTintClass)}>
      <span className={`staging-issue-icon staging-issue-icon-${file.issue}`} aria-hidden="true">
        <IssueIcon />
      </span>
      <div className="staging-file-info">
        <span className="staging-file-name">{file.filename}</span>
        <Chip tone={ISSUE_TONE[file.issue]} className="staging-issue-badge">
          {RESOLUTION_ISSUE_LABEL[file.issue].toUpperCase()}
        </Chip>
        {analysisSignal && (
          <Chip
            tone={analysisSignal.disposition === 'auto-quote' ? 'success' : 'warning'}
            className="staging-row-signal-chip"
            title={ANALYSIS_SIGNAL_REASON_LABEL[analysisSignal.reason]}
          >
            {ANALYSIS_SIGNAL_REASON_LABEL[analysisSignal.reason]}
          </Chip>
        )}
        <p className="caption-text staging-file-description">{ISSUE_DESCRIPTION[file.issue]}</p>
      </div>
      <div className="staging-file-actions">
        {isNoMatch ? (
          <>
            <DropdownMenu
              menuLabel={`Match ${file.filename} to a catalog item (demo)`}
              renderTrigger={({ open, toggle, triggerRef }) => (
                <button
                  ref={triggerRef}
                  type="button"
                  className="btn btn-outline btn-sm"
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  onClick={toggle}
                >
                  <SearchOutlined aria-hidden="true" /> Match to catalog…
                </button>
              )}
              renderMenu={(close) => (
                <div className="staging-catalog-search-panel">
                  <div className="dropdown-menu-header">
                    Match to catalog <Chip tone="warning">DEMO</Chip>
                  </div>
                  <p className="caption-text staging-catalog-search-note">
                    Illustrative results only — not a real pricing source.
                  </p>
                  <form
                    className="staging-catalog-search-form"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void handleCatalogSearch()
                    }}
                  >
                    <label className="sr-only" htmlFor={`catalog-search-${file.fileId}`}>
                      Search the demo catalog
                    </label>
                    <input
                      id={`catalog-search-${file.fileId}`}
                      type="search"
                      placeholder="Search by name, MPN, or vendor…"
                      value={catalogQuery}
                      onChange={(event) => setCatalogQuery(event.target.value)}
                    />
                    <Button type="submit" size="sm" loading={catalogBusy}>
                      Search
                    </Button>
                  </form>
                  {catalogError && (
                    <p role="alert" className="staging-row-inline-error">
                      {catalogError}
                    </p>
                  )}
                  {catalogResults !== null && (
                    <ul className="staging-catalog-results">
                      {catalogResults.length === 0 && (
                        <li className="dropdown-menu-empty">No demo catalog matches found.</li>
                      )}
                      {catalogResults.map((suggestion) => (
                        <li key={suggestion.id}>
                          <button
                            type="button"
                            className="dropdown-menu-item staging-catalog-result"
                            onClick={() => {
                              onMatchToCatalog(suggestion.id)
                              close()
                            }}
                          >
                            <img
                              className="staging-row-thumb"
                              src={suggestion.thumbnailUrl ?? undefined}
                              alt=""
                              aria-hidden="true"
                              style={suggestion.thumbnailUrl ? undefined : { visibility: 'hidden' }}
                            />
                            <span className="parts-row-name">
                              <span className="parts-row-name-text" title={suggestion.name}>
                                {suggestion.name}
                              </span>
                              <span className="caption-text">
                                {suggestion.mpn} · {suggestion.vendor}
                              </span>
                            </span>
                            <span className="caption-text">
                              {suggestion.indicativePrice
                                ? formatPrice(suggestion.indicativePrice)
                                : '—'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            />
            {/* OQ-13 (ADR-0079) — "Create new part" supplements "Keep as
                custom" on the no-match branch; both resolve via the existing
                `resolveFile` endpoint (`create`/`keep-as-custom` are already
                `FileResolutionAction` members) and neither replaces the
                other. */}
            <Button variant="outline" size="sm" onClick={onCreatePart}>
              Create new part
            </Button>
            <Button variant="outline" size="sm" onClick={onKeepAsCustom}>
              Keep as custom
            </Button>
          </>
        ) : (
          <>
            <DropdownMenu
              menuLabel={`Attach ${file.filename} to a part`}
              renderTrigger={({ open, toggle, triggerRef }) => (
                <button
                  ref={triggerRef}
                  type="button"
                  className="btn btn-outline btn-sm"
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={toggle}
                >
                  Attach to part
                </button>
              )}
              renderMenu={(close) => (
                <>
                  <div className="dropdown-menu-header">Attach to an identified part</div>
                  {identifiedParts.length === 0 && (
                    <p className="dropdown-menu-empty">No identified parts yet.</p>
                  )}
                  {identifiedParts.map((part) => (
                    <button
                      key={part.partId}
                      type="button"
                      role="menuitem"
                      className="dropdown-menu-item"
                      title={part.name}
                      onClick={() => {
                        onAssign(part.partId)
                        close()
                      }}
                    >
                      <span className="parts-row-name-text">{part.name}</span>
                    </button>
                  ))}
                </>
              )}
            />
            <Button variant="outline" size="sm" onClick={onCreatePart}>
              Create new part
            </Button>
          </>
        )}
        <button
          type="button"
          className="uploads-trash-btn"
          aria-label={`Remove ${file.filename}`}
          onClick={onRemove}
        >
          <DeleteOutlined aria-hidden="true" />
        </button>
      </div>
    </li>
  )
}
